import React from 'react';
import { Link } from 'react-router-dom';
import { Table, Button, Modal, Form, Select, DatePicker, Input, notification, Typography, Space, Tag } from 'antd';
import { PlusOutlined, CheckOutlined } from '@ant-design/icons';
import moment from 'moment';

import { useAuth } from '../../authentication/AuthProvider';
import request from '../../lib/request';
import { useCompanyId } from '../../lib/books';

import { date as fmtDate, label } from '../../lib/format';
const STATUS_COLORS = { PENDING: 'blue', FILED: 'green', OVERDUE: 'red' };

/** Firm-wide compliance deadline list - the company's own deadlines plus its clients'. */
export default function DeadlinesPage() {
  const auth = useAuth();
  const isSystemAdmin = auth.user.role === 'SYSTEM_ADMIN';
  const { companyId, setCompanyId } = useCompanyId();
  const [companies, setCompanies] = React.useState([]);
  const [deadlines, setDeadlines] = React.useState([]);
  const [clients, setClients] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [modalOpen, setModalOpen] = React.useState(false);
  const [form] = Form.useForm();

  React.useEffect(() => {
    if (isSystemAdmin) {
      request
        .get('/companies')
        .then((r) => r.json())
        .then((r) => {
          if (r.status) {
            setCompanies(r.data);
            if (!companyId && r.data.length) setCompanyId(r.data[0].id);
          }
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDeadlines = () => {
    if (!companyId) return;
    setLoading(true);
    request
      .get('/deadlines', { company_id: companyId })
      .then((r) => r.json())
      .then((r) => r.status && setDeadlines(r.data))
      .finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(fetchDeadlines, [companyId]);
  React.useEffect(() => {
    if (!companyId) return;
    request.get('/clients', { company_id: companyId }).then((r) => r.json()).then((r) => r.status && setClients(r.data));
  }, [companyId]);

  const handleCreate = () => {
    form.validateFields().then((values) => {
      request
        .post('/deadlines', { ...values, client_id: values.client_id || null, company_id: values.client_id ? null : companyId, due_date: values.due_date.format('YYYY-MM-DD') })
        .then((r) => r.json())
        .then((r) => {
          if (r.status) {
            notification.success({ message: 'Deadline created' });
            setModalOpen(false);
            fetchDeadlines();
          } else {
            notification.error({ message: r.message });
          }
        });
    });
  };

  const handleMarkFiled = (deadline) => {
    request
      .put(`/deadlines/${deadline.id}`, { status: 'FILED', filed_date: moment().format('YYYY-MM-DD') })
      .then((r) => r.json())
      .then((r) => {
        if (r.status) {
          notification.success({ message: 'Marked as filed' });
          fetchDeadlines();
        } else {
          notification.error({ message: r.message });
        }
      });
  };

  const columns = [
    { title: 'Due Date', dataIndex: 'due_date', sorter: (a, b) => (a.due_date < b.due_date ? -1 : 1), render: (v) => fmtDate(v) },
    { title: 'Type', dataIndex: 'deadline_type' },
    { title: 'Period', dataIndex: 'period' },
    {
      title: 'For',
      key: 'for',
      render: (_, d) => (d.Client ? <Link to={`/clients/${d.Client.id}`}>{d.Client.client_name}</Link> : 'This Company'),
    },
    { title: 'Status', dataIndex: 'status', render: (v) => <Tag color={STATUS_COLORS[v]}>{label(v)}</Tag> },
    { title: 'Notes', dataIndex: 'notes', ellipsis: true },
    {
      title: '',
      key: 'actions',
      render: (_, d) =>
        d.status !== 'FILED' && (
          <Button size="small" icon={<CheckOutlined />} onClick={() => handleMarkFiled(d)}>
            Mark Filed
          </Button>
        ),
    },
  ];

  return (
    <div>
      <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          Deadlines
        </Typography.Title>
        <Space>
          {isSystemAdmin && (
            <Select
              style={{ width: 200 }}
              value={companyId}
              placeholder="Company"
              onChange={setCompanyId}
              options={companies.map((c) => ({ value: c.id, label: c.name }))}
            />
          )}
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              form.resetFields();
              setModalOpen(true);
            }}
          >
            New Deadline
          </Button>
        </Space>
      </Space>

      <Table rowKey="id" columns={columns} dataSource={deadlines} loading={loading} />

      <Modal title="New Deadline" open={modalOpen} onCancel={() => setModalOpen(false)} onOk={handleCreate}>
        <Form form={form} layout="vertical">
          <Form.Item name="client_id" label="Client" extra="Leave empty for the firm's own deadline">
            <Select allowClear showSearch optionFilterProp="label" placeholder="This company's own deadline" options={clients.map((c) => ({ value: c.id, label: c.client_name }))} />
          </Form.Item>
          <Form.Item name="deadline_type" label="Type" initialValue="OTHER">
            <Select options={['VAT', 'VIES', 'OSS', 'IOSS', 'OTHER'].map((v) => ({ value: v, label: v }))} />
          </Form.Item>
          <Form.Item name="period" label="Period">
            <Input placeholder="e.g. Q3 2026" />
          </Form.Item>
          <Form.Item name="due_date" label="Due Date" rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
